import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import {
	Button,
	Card,
	Col,
	Form,
	InputNumber,
	Select,
	Space,
	Table,
	Typography,
	Row,
	Tooltip,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import L from "leaflet";
import { FeatureGroup, MapContainer, Marker, Polygon, Popup, TileLayer, useMapEvents } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

import PageTitle from "../../page-header/PageHeader";
import {
	loadProjectSiteDetails,
} from "../../../redux/rtk/features/projectManagement/project/projectSite/projectSiteDetails";
import {
	loadProjectSiteAreas,
	saveProjectSiteArea,
} from "../../../redux/rtk/features/projectManagement/project/projectSite/projectSiteArea";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl:
		"https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
	iconUrl:
		"https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
	shadowUrl:
		"https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png",
});

const DEFAULT_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 5;
const FOCUSED_ZOOM = 15;

const createEmptyPoint = (order = 0) => ({
	Latitude: null,
	Longitude: null,
	PointOrder: order,
});

const EMPTY_POINT = createEmptyPoint();

const isPointString = (value = "") => /^POINT\s*\(([-+0-9.]+)\s+([-+0-9.]+)\)$/i.test(value.trim());

const parsePointString = (value = "") => {
	const match = value.trim().match(/^POINT\s*\(([-+0-9.]+)\s+([-+0-9.]+)\)$/i);
	if (!match) {
		return null;
	}
	const [, lngStr, latStr] = match;
	const lng = Number(lngStr);
	const lat = Number(latStr);
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
		return null;
	}
	return {
		Latitude: lat,
		Longitude: lng,
		PointOrder: 0,
	};
};

const normalizePoints = (area) => {
	if (!area) {
		return [];
	}
	if (typeof area === "string") {
		const trimmed = area.trim();
		if (!trimmed) {
			return [];
		}
		if (isPointString(trimmed)) {
			const point = parsePointString(trimmed);
			return point ? [point] : [];
		}
		try {
			const parsed = JSON.parse(trimmed);
			return normalizePoints(parsed);
		} catch (error) {
			return [];
		}
	}
	if (Array.isArray(area?.points)) {
		return area.points
			.map((point, index) => ({
				Latitude:
					point.lat !== undefined && point.lat !== null
						? Number(point.lat)
						: point.y !== undefined && point.y !== null
							? Number(point.y)
							: null,
				Longitude:
					point.lng !== undefined && point.lng !== null
						? Number(point.lng)
						: point.x !== undefined && point.x !== null
							? Number(point.x)
							: null,
				PointOrder: index,
			}))
			.filter(
				(point) => Number.isFinite(point.Latitude) && Number.isFinite(point.Longitude)
			);
	}
	return [];
};

const buildPointsFromAreaRecords = (records = []) =>
	records
		.map((record) => ({
			Latitude:
				record.Latitude !== undefined && record.Latitude !== null
					? Number(record.Latitude)
					: null,
			Longitude:
				record.Longitude !== undefined && record.Longitude !== null
					? Number(record.Longitude)
					: null,
			PointOrder:
				record.PointOrder !== undefined && record.PointOrder !== null
					? Number(record.PointOrder)
					: null,
		}))
		.filter(
			(point) => Number.isFinite(point.Latitude) && Number.isFinite(point.Longitude)
		)
		.sort((a, b) => (a.PointOrder ?? 0) - (b.PointOrder ?? 0));

const sanitizePointsForSave = (points = []) =>
	points
		.map((point, index) => ({
			Latitude:
				point.Latitude !== undefined && point.Latitude !== null
					? Number(point.Latitude)
					: null,
			Longitude:
				point.Longitude !== undefined && point.Longitude !== null
					? Number(point.Longitude)
					: null,
			PointOrder:
				point.PointOrder !== undefined && point.PointOrder !== null
					? Number(point.PointOrder)
					: index,
		}))
		.filter(
			(point) => Number.isFinite(point.Latitude) && Number.isFinite(point.Longitude)
		);

const convertPointsToCoordinates = (points = []) => {
	const coords = points
		.filter(
			(point) =>
				point &&
				Number.isFinite(point.Latitude) &&
				Number.isFinite(point.Longitude)
		)
		.map((point) => [Number(point.Latitude), Number(point.Longitude)]);
	return normalizeCoordinateLoop(coords);
};

const coordinatesToPoints = (coords = []) =>
	Array.isArray(coords)
		? coords
				.filter(
					(coord) =>
						Array.isArray(coord) &&
						coord.length === 2 &&
						Number.isFinite(coord[0]) &&
						Number.isFinite(coord[1])
				)
				.map((coord, index) => ({
					Latitude: Number(coord[0]),
					Longitude: Number(coord[1]),
					PointOrder: index,
				}))
		: [];

const calculatePolygonCenter = (coords = []) => {
	if (!Array.isArray(coords) || !coords.length) {
		return DEFAULT_CENTER;
	}
	let sumLat = 0;
	let sumLng = 0;
	let count = 0;
	coords.forEach((coord) => {
		if (
			Array.isArray(coord) &&
			coord.length === 2 &&
			Number.isFinite(coord[0]) &&
			Number.isFinite(coord[1])
		) {
			sumLat += coord[0];
			sumLng += coord[1];
			count += 1;
		}
	});
	if (!count) {
		return DEFAULT_CENTER;
	}
	return [sumLat / count, sumLng / count];
};

const getLatLngsFromLayer = (layer) => {
	if (!layer) {
		return [];
	}
	let latLngs = layer.getLatLngs();
	if (Array.isArray(latLngs[0])) {
		latLngs = latLngs[0];
	}
	return latLngs
		.map((latlng) => [latlng.lat, latlng.lng])
		.filter(
			(coord) =>
				Array.isArray(coord) &&
				coord.length === 2 &&
				Number.isFinite(coord[0]) &&
				Number.isFinite(coord[1])
		);
};

const normalizeCoordinateLoop = (coords = []) => {
	if (!Array.isArray(coords) || coords.length < 2) {
		return coords;
	}
	const first = coords[0];
	const last = coords[coords.length - 1];
	if (
		Array.isArray(first) &&
		Array.isArray(last) &&
		first.length === 2 &&
		last.length === 2 &&
		Math.abs(first[0] - last[0]) < 1e-9 &&
		Math.abs(first[1] - last[1]) < 1e-9
	) {
		return coords.slice(0, -1);
	}
	return coords;
};

// Polyline decoder function
const decodePolyline = (encoded) => {
	const coordinates = [];
	let index = 0;
	const len = encoded.length;
	let lat = 0;
	let lng = 0;

	while (index < len) {
		let b;
		let shift = 0;
		let result = 0;
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);
		const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
		lat += dlat;

		shift = 0;
		result = 0;
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);
		const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
		lng += dlng;

		coordinates.push([lat * 1e-5, lng * 1e-5]);
	}

	return coordinates;
};

// Convert meters to degrees (approximate, using average latitude)
const metersToDegrees = (meters, latitude) => {
	const latRad = (latitude * Math.PI) / 180;
	const metersPerDegreeLat = 111320; // meters per degree of latitude
	const metersPerDegreeLng = 111320 * Math.cos(latRad); // meters per degree of longitude (varies with latitude)
	
	return {
		lat: meters / metersPerDegreeLat,
		lng: meters / metersPerDegreeLng
	};
};

// Calculate bearing between two points
const calculateBearing = (point1, point2) => {
	const [lat1, lng1] = point1;
	const [lat2, lng2] = point2;
	const dLng = (lng2 - lng1) * Math.PI / 180;
	const lat1Rad = lat1 * Math.PI / 180;
	const lat2Rad = lat2 * Math.PI / 180;
	
	const y = Math.sin(dLng) * Math.cos(lat2Rad);
	const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
	
	const bearing = Math.atan2(y, x);
	return (bearing * 180 / Math.PI + 360) % 360;
};

// Calculate perpendicular offset point using spherical geometry
const offsetPoint = (point, bearing, distanceInDegrees) => {
	const [lat, lng] = point;
	const bearingRad = (bearing * Math.PI) / 180;
	const latRad = (lat * Math.PI) / 180;
	
	// Calculate offset in degrees
	// For latitude: offset is proportional to cos(bearing)
	// For longitude: offset is proportional to sin(bearing), adjusted for latitude
	const offsetLat = distanceInDegrees.lat * Math.cos(bearingRad);
	const offsetLng = distanceInDegrees.lng * Math.sin(bearingRad) / Math.cos(latRad);
	
	return [lat + offsetLat, lng + offsetLng];
};

// Generate buffer polygon around a route line
const generateBufferPolygon = (routeCoordinates, leftMeters, rightMeters) => {
	if (!Array.isArray(routeCoordinates) || routeCoordinates.length < 2) {
		return [];
	}
	
	if (leftMeters <= 0 && rightMeters <= 0) {
		return [];
	}
	
	const leftPoints = [];
	const rightPoints = [];
	
	// Process each point along the route
	for (let i = 0; i < routeCoordinates.length; i++) {
		const currentPoint = routeCoordinates[i];
		const [currentLat] = currentPoint;
		
		// Calculate meter-to-degree conversion for this specific point's latitude
		const leftDegrees = metersToDegrees(leftMeters, currentLat);
		const rightDegrees = metersToDegrees(rightMeters, currentLat);
		
		let bearing;
		
		if (i === 0) {
			// First point: use bearing to next point
			bearing = calculateBearing(currentPoint, routeCoordinates[i + 1]);
		} else if (i === routeCoordinates.length - 1) {
			// Last point: use bearing from previous point
			bearing = calculateBearing(routeCoordinates[i - 1], currentPoint);
		} else {
			// Middle point: use average bearing of incoming and outgoing segments
			const bearing1 = calculateBearing(routeCoordinates[i - 1], currentPoint);
			const bearing2 = calculateBearing(currentPoint, routeCoordinates[i + 1]);
			// Average bearing, handling wrap-around
			let avgBearing = (bearing1 + bearing2) / 2;
			if (Math.abs(bearing1 - bearing2) > 180) {
				avgBearing = (avgBearing + 180) % 360;
			}
			bearing = avgBearing;
		}
		
		// Calculate perpendicular bearing (90 degrees to the right of route direction)
		const perpendicularBearingRight = (bearing + 90) % 360;
		const perpendicularBearingLeft = (bearing - 90 + 360) % 360;
		
		// Calculate offset points
		if (leftMeters > 0) {
			leftPoints.push(offsetPoint(currentPoint, perpendicularBearingLeft, leftDegrees));
		}
		
		if (rightMeters > 0) {
			rightPoints.push(offsetPoint(currentPoint, perpendicularBearingRight, rightDegrees));
		}
	}
	
	// Combine points to form closed polygon
	const bufferPolygon = [];
	
	// Add left points (in forward order along the route)
	if (leftPoints.length > 0) {
		bufferPolygon.push(...leftPoints);
	}
	
	// Add right points (in reverse order to close the polygon)
	if (rightPoints.length > 0) {
		bufferPolygon.push(...rightPoints.reverse());
	}
	
	// Close the polygon by adding first point again if we have enough points
	if (bufferPolygon.length >= 3) {
		bufferPolygon.push([bufferPolygon[0][0], bufferPolygon[0][1]]);
	}
	
	return bufferPolygon;
};

const ProjectSiteArea = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const [form] = Form.useForm();
	const [selectedSiteId, setSelectedSiteId] = useState(null);
	const [editingSiteId, setEditingSiteId] = useState(null);
	const [drawnCoordinates, setDrawnCoordinates] = useState([]);
	const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
	const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
	const [manualLocation, setManualLocation] = useState({ lat: '', lng: '' });
	const [routeStart, setRouteStart] = useState({ lat: '', lng: '' });
	const [routeEnd, setRouteEnd] = useState({ lat: '', lng: '' });
	const [isUpdatingFromMap, setIsUpdatingFromMap] = useState(false);
	const [isLocating, setIsLocating] = useState(false);
	const [isGeneratingRoute, setIsGeneratingRoute] = useState(false);
	const [geofenceLeftMeters, setGeofenceLeftMeters] = useState('');
	const [geofenceRightMeters, setGeofenceRightMeters] = useState('');
	const [geofenceCoordinates, setGeofenceCoordinates] = useState([]);
	const [showGeofenceUI, setShowGeofenceUI] = useState(false);
	const mapRef = useRef(null);
	const featureGroupRef = useRef(null);
	const [pendingSiteId, setPendingSiteId] = useState(() => {
		const rawFromState =
			location?.state?.projectSiteId ?? location?.state?.ProjectSiteId ?? null;
		const numeric = Number(rawFromState);
		return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
	});

	const safeMapAction = useCallback((action) => {
		const map = mapRef.current;
		if (!map || !map._loaded || !map._mapPane) {
			return;
		}
		try {
			action(map);
		} catch {
			// ignore map animation errors
		}
	}, []);

	const applyCoordinatesToState = useCallback(
		(coords = []) => {
			const validCoords = Array.isArray(coords) ? coords : [];
			setDrawnCoordinates(validCoords);
			if (validCoords.length) {
				const center = calculatePolygonCenter(validCoords);
				setMapCenter(center);
				setMapZoom(FOCUSED_ZOOM);
				safeMapAction((map) => {
					if (validCoords.length > 1) {
						map.fitBounds(
							L.latLngBounds(validCoords.map(([lat, lng]) => [lat, lng]))
						);
					} else {
						map.setView(validCoords[0], FOCUSED_ZOOM);
					}
				});
			} else {
				setMapCenter(DEFAULT_CENTER);
				setMapZoom(DEFAULT_ZOOM);
				safeMapAction((map) => {
					map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
				});
			}
		},
		[safeMapAction]
	);

	const { list: siteList = [], loading: siteLoading } = useSelector(
		(state) => state.projectSiteDetails || {}
	);
	const {
		list: areaList = [],
		loading: areaLoading,
	} = useSelector((state) => state.projectSiteArea || {});
	const savingArea = areaLoading;

	useEffect(() => {
		dispatch(loadProjectSiteDetails());
		dispatch(loadProjectSiteAreas());
	}, [dispatch]);

	useEffect(() => {
		const rawFromState =
			location?.state?.projectSiteId ?? location?.state?.ProjectSiteId ?? null;
		const numeric = Number(rawFromState);
		if (Number.isFinite(numeric) && numeric > 0) {
			setPendingSiteId(numeric);
		}
	}, [location]);

	// When there is coordinate data, fill Start with first point and End with last point
	useEffect(() => {
		if (drawnCoordinates && drawnCoordinates.length >= 2) {
			const first = drawnCoordinates[0];
			const last = drawnCoordinates[drawnCoordinates.length - 1];
			if (Array.isArray(first) && first.length >= 2 && Array.isArray(last) && last.length >= 2) {
				const [startLat, startLng] = first;
				const [endLat, endLng] = last;
				setRouteStart({
					lat: Number.isFinite(startLat) ? startLat : '',
					lng: Number.isFinite(startLng) ? startLng : '',
				});
				setRouteEnd({
					lat: Number.isFinite(endLat) ? endLat : '',
					lng: Number.isFinite(endLng) ? endLng : '',
				});
			}
		}
	}, [drawnCoordinates]);

	const siteOptions = useMemo(
		() =>
			siteList.map((site) => ({
				value: Number(site.ProjectSiteId),
				label: site.ProjectSiteName,
			})),
		[siteList]
	);

	const handleSiteChange = useCallback(
		(value) => {
			setSelectedSiteId(value || null);
			if (!value) {
				setEditingSiteId(null);
				form.setFieldsValue({
					ProjectSiteId: 0,
					SelectedProjectSite: null,
					points: [createEmptyPoint()],
				});
				applyCoordinatesToState([]);
				return;
			}
			const site = siteList.find(
				(item) => Number(item.ProjectSiteId) === Number(value)
			);
			const areaRecords = areaList.filter(
				(item) => Number(item.ProjectSiteId) === Number(value)
			);
			setEditingSiteId(areaRecords.length ? Number(value) : null);
			const areaPoints = buildPointsFromAreaRecords(areaRecords);
			const geometryPoints = normalizePoints(site?.ProjectSiteArea);
			const pointsToUse = (areaPoints.length ? areaPoints : geometryPoints).map((point, index) => ({
				Latitude: point.Latitude,
				Longitude: point.Longitude,
				PointOrder: point.PointOrder ?? index,
			}));
			const formPoints = pointsToUse.length ? pointsToUse : [createEmptyPoint()];
			form.setFieldsValue({
				ProjectSiteId: Number(value),
				SelectedProjectSite: Number(value),
				points: formPoints,
			});

			const coords = convertPointsToCoordinates(formPoints);
			applyCoordinatesToState(coords);
		},
		[areaList, applyCoordinatesToState, form, siteList]
	);

	useEffect(() => {
		if (pendingSiteId === null || pendingSiteId === undefined) {
			return;
		}
		const numeric = Number(pendingSiteId);
		if (!Number.isFinite(numeric) || numeric <= 0) {
			return;
		}
		const siteExists = siteList.some(
			(item) => Number(item.ProjectSiteId) === numeric
		);
		if (!siteExists) {
			return;
		}
		setPendingSiteId(null);
		handleSiteChange(numeric);
	}, [handleSiteChange, pendingSiteId, siteList]);

	const handleDrawLayerChange = useCallback(
		(coords) => {
			const normalizedCoords = normalizeCoordinateLoop(coords);
			setIsUpdatingFromMap(true);
			applyCoordinatesToState(normalizedCoords);
			const points =
				Array.isArray(normalizedCoords) && normalizedCoords.length
					? normalizedCoords.map((coord, index) => ({
							Latitude: Number(coord[0]),
							Longitude: Number(coord[1]),
							PointOrder: index,
					  }))
					: [createEmptyPoint()];
			form.setFieldsValue({ points });
		},
		[applyCoordinatesToState, form]
	);

	const handleDrawCreated = useCallback(
		(e) => {
			if (!e?.layer) {
				return;
			}
			const coords = getLatLngsFromLayer(e.layer);
			handleDrawLayerChange(coords);
		},
		[handleDrawLayerChange]
	);

	const handleDrawEdited = useCallback(
		(e) => {
			let coords = [];
			if (e?.layers) {
				e.layers.eachLayer((layer) => {
					const layerCoords = getLatLngsFromLayer(layer);
					if (layerCoords.length) {
						coords = layerCoords;
					}
				});
			}
			handleDrawLayerChange(coords);
		},
		[handleDrawLayerChange]
	);

	const handleDrawDeleted = useCallback(() => {
		handleDrawLayerChange([]);
	}, [handleDrawLayerChange]);

	const handleNavigateToCoordinates = useCallback(() => {
		const lat = parseFloat(manualLocation.lat);
		const lng = parseFloat(manualLocation.lng);
		
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			window.alert("Please enter valid latitude and longitude values.");
			return;
		}
		
		if (lat < -90 || lat > 90) {
			window.alert("Latitude must be between -90 and 90.");
			return;
		}
		
		if (lng < -180 || lng > 180) {
			window.alert("Longitude must be between -180 and 180.");
			return;
		}
		
		const latlng = [lat, lng];
		setMapCenter(latlng);
		setMapZoom(FOCUSED_ZOOM);
		safeMapAction((map) => {
			map.flyTo(latlng, FOCUSED_ZOOM);
		});
	}, [manualLocation, safeMapAction]);

	const handleGetCurrentLocation = useCallback(() => {
		if (!navigator.geolocation) {
			window.alert("Geolocation is not supported by your browser.");
			return;
		}
		setIsLocating(true);
		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords;
				const currentLocation = [latitude, longitude];
				setManualLocation({ lat: latitude.toFixed(6), lng: longitude.toFixed(6) });
				setMapCenter(currentLocation);
				setMapZoom(FOCUSED_ZOOM);
				safeMapAction((map) => {
					map.flyTo(currentLocation, FOCUSED_ZOOM);
				});
				setIsLocating(false);
			},
			(error) => {
				// console.error("Error fetching current location:", error);
				window.alert("Unable to retrieve your location. Please check permissions.");
				setIsLocating(false);
			},
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 0,
			}
		);
	}, []);

	const handleGenerateRoute = useCallback(async () => {
		const startLat = parseFloat(routeStart.lat);
		const startLng = parseFloat(routeStart.lng);
		const endLat = parseFloat(routeEnd.lat);
		const endLng = parseFloat(routeEnd.lng);

		if (!Number.isFinite(startLat) || !Number.isFinite(startLng) || 
			!Number.isFinite(endLat) || !Number.isFinite(endLng)) {
			window.alert("Please enter valid start and destination coordinates.");
			return;
		}

		if (startLat < -90 || startLat > 90 || endLat < -90 || endLat > 90) {
			window.alert("Latitude must be between -90 and 90.");
			return;
		}

		if (startLng < -180 || startLng > 180 || endLng < -180 || endLng > 180) {
			window.alert("Longitude must be between -180 and 180.");
			return;
		}

		setIsGeneratingRoute(true);
		try {
			const url = `https://routing.openstreetmap.de/routed-car/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?overview=false&geometries=polyline&steps=true`;
			
			const response = await fetch(url);
			const data = await response.json();

			if (data.code === "Ok" && data.routes && data.routes.length > 0) {
				const route = data.routes[0];
				const allCoordinates = [];

				// Extract coordinates from all legs and steps
				if (route.legs && Array.isArray(route.legs)) {
					route.legs.forEach(leg => {
						if (leg.steps && Array.isArray(leg.steps)) {
							leg.steps.forEach(step => {
								if (step.geometry) {
									const decoded = decodePolyline(step.geometry);
									// Decoded coordinates are in [lat, lng] format
									allCoordinates.push(...decoded);
								}
							});
						}
					});
				}

				if (allCoordinates.length > 0) {
					// Remove very close duplicate points (within 0.0001 degrees ~ 11 meters)
					const uniqueCoords = [];
					const tolerance = 0.0001;
					
					allCoordinates.forEach(coord => {
						if (uniqueCoords.length === 0) {
							uniqueCoords.push([coord[0], coord[1]]);
						} else {
							const lastCoord = uniqueCoords[uniqueCoords.length - 1];
							const latDiff = Math.abs(coord[0] - lastCoord[0]);
							const lngDiff = Math.abs(coord[1] - lastCoord[1]);
							
							// Only add if significantly different from last point
							if (latDiff > tolerance || lngDiff > tolerance) {
								uniqueCoords.push([coord[0], coord[1]]);
							}
						}
					});

					// Ensure we have at least 2 points
					if (uniqueCoords.length >= 2) {
						// Update form with route coordinates
						const routePoints = uniqueCoords.map((coord, index) => ({
							Latitude: coord[0],
							Longitude: coord[1],
							PointOrder: index,
						}));

						setIsUpdatingFromMap(true);
						form.setFieldsValue({ points: routePoints });
						
						// Apply coordinates to map (will display as polygon/area)
						applyCoordinatesToState(uniqueCoords);
						
						window.alert(`Route generated successfully with ${uniqueCoords.length} points!`);
					} else {
						window.alert("Route generated but has insufficient points.");
					}
				} else {
					window.alert("No route found. Please check your coordinates.");
				}
			} else {
				window.alert(data.message || "Failed to generate route. Please try again.");
			}
		} catch (error) {
			// console.error("Error generating route:", error);
			window.alert("Error generating route. Please check your internet connection and try again.");
		} finally {
			setIsGeneratingRoute(false);
		}
	}, [routeStart, routeEnd, form, applyCoordinatesToState]);

	const handleGenerateGeofence = useCallback(() => {
		const leftMeters = parseFloat(geofenceLeftMeters);
		const rightMeters = parseFloat(geofenceRightMeters);

		// Validate meters
		if (!Number.isFinite(leftMeters) || !Number.isFinite(rightMeters) || 
			leftMeters <= 0 || rightMeters <= 0) {
			window.alert("Please enter valid left and right meter distances (must be greater than 0).");
			return;
		}

		// Check if we have a route drawn
		if (!drawnCoordinates || drawnCoordinates.length < 2) {
			window.alert("Please generate a route first (using the 🚗 button) or draw a route manually on the map.");
			return;
		}

		// Generate buffer polygon
		const bufferPolygon = generateBufferPolygon(drawnCoordinates, leftMeters, rightMeters);
		
		if (bufferPolygon.length === 0) {
			window.alert("Failed to generate geofence. Please check your inputs.");
			return;
		}

		// Set geofence coordinates
		setGeofenceCoordinates(bufferPolygon);
		
		// Update form with geofence coordinates
		const geofencePoints = bufferPolygon.slice(0, -1).map((coord, index) => ({
			Latitude: coord[0],
			Longitude: coord[1],
			PointOrder: index,
		}));

		setIsUpdatingFromMap(true);
		form.setFieldsValue({ points: geofencePoints });
		
		// Apply geofence coordinates to map
		applyCoordinatesToState(bufferPolygon.slice(0, -1));
		
		window.alert(`Geofence generated successfully with ${bufferPolygon.length - 1} points!`);
	}, [geofenceLeftMeters, geofenceRightMeters, drawnCoordinates, form, applyCoordinatesToState]);

	const LocateControl = () => {
		const mapInstance = useMapEvents({
			click(e) {
				// On map click, just update coordinates to clicked location (don't get current location)
				const { lat, lng } = e.latlng;
				const clickedLocation = [lat, lng];
				
				// Update manual location state with clicked coordinates
				setManualLocation({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
				
				// Navigate map to clicked location
				setMapCenter(clickedLocation);
				setMapZoom(FOCUSED_ZOOM);
				safeMapAction((map) => {
					map.flyTo(clickedLocation, FOCUSED_ZOOM);
				});
			},
		});

		useEffect(() => {
			mapRef.current = mapInstance;
		}, [mapInstance]);

		return null;
	};

	const handleFormValuesChange = useCallback(
		(changedValues, allValues) => {
			if (isUpdatingFromMap) {
				setIsUpdatingFromMap(false);
				return;
			}
			if (changedValues?.points) {
				const coords = convertPointsToCoordinates(allValues?.points || []);
				applyCoordinatesToState(coords);
			}
		},
		[applyCoordinatesToState, isUpdatingFromMap]
	);

	const handleFinish = useCallback(
		async (values) => {
			const { points = [] } = values;
			const sanitized = sanitizePointsForSave(points);
			if (!selectedSiteId || !sanitized.length) {
				return;
			}
			const payload = {
				ProjectSiteId: Number(selectedSiteId),
				PointsDetails: sanitized,
				DataStatus: 1,
			};
			const response = await dispatch(saveProjectSiteArea(payload));
			if (response?.meta?.requestStatus !== "rejected") {
				dispatch(loadProjectSiteAreas());
				dispatch(loadProjectSiteDetails());
				setEditingSiteId(null);
				setSelectedSiteId(null);
				form.setFieldsValue({
					ProjectSiteId: 0,
					SelectedProjectSite: null,
					points: [createEmptyPoint()],
				});
				applyCoordinatesToState([]);
			}
		},
		[applyCoordinatesToState, dispatch, form, selectedSiteId]
	);

	const handleAddNew = useCallback(() => {
		setEditingSiteId(null);
		setSelectedSiteId(null);
		form.setFieldsValue({
			ProjectSiteId: 0,
			SelectedProjectSite: null,
			points: [createEmptyPoint()],
		});
		applyCoordinatesToState([]);
	}, [applyCoordinatesToState, form]);

	const isLogged = Boolean(localStorage.getItem("isLogged"));
	if (!isLogged) {
		return <Navigate to="/admin/auth/login" replace />;
	}

	return (
		<div>
			
			<Card
				title={<Typography.Title level={4} style={{ margin: 0 }}>Project Site Area</Typography.Title>}
				extra={
					<Space>
						<Button 
							type="primary" 
							onClick={() => form.submit()} 
							loading={savingArea} 
							disabled={savingArea}
						>
							{editingSiteId ? "Update Area" : "Save Area"}
						</Button>
						<Button
							type="default"
							onClick={() => setShowGeofenceUI((prev) => !prev)}
						>
							{showGeofenceUI ? "Hide Geofence" : "Generate Geofence (Buffer Zone)"}
						</Button>
						<Button onClick={handleAddNew} disabled={savingArea}>New Entry</Button>
					</Space>
				}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleFinish}
				onValuesChange={handleFormValuesChange}
					initialValues={{ ProjectSiteId: 0, SelectedProjectSite: null, points: [createEmptyPoint()] }}
				>
					<Form.Item name="ProjectSiteId" hidden>
						<InputNumber style={{ display: "none" }} />
					</Form.Item>
					<Row gutter={16}>
						<Col xs={24} md={12}>
							<Form.Item
								label="Select Project Site"
								name="SelectedProjectSite"
								rules={[{ required: true, message: "Select a project site" }]}
							>
								<Select
									showSearch
									placeholder="Choose a project site"
									options={siteOptions}
									loading={siteLoading || areaLoading}
									optionFilterProp="label"
									onChange={handleSiteChange}
									allowClear
									value={selectedSiteId}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Row gutter={8} align="bottom">
								<Col xs={7}>
									<Form.Item label="Latitude">
										<InputNumber
											style={{ width: "100%" }}
											placeholder="Latitude"
											min={-90}
											max={90}
											step={0.000001}
											value={manualLocation.lat}
											onChange={(value) => setManualLocation(prev => ({ ...prev, lat: value ?? '' }))}
										/>
									</Form.Item>
								</Col>
								<Col xs={7}>
									<Form.Item label="Longitude">
										<InputNumber
											style={{ width: "100%" }}
											placeholder="Longitude"
											min={-180}
											max={180}
											step={0.000001}
											value={manualLocation.lng}
											onChange={(value) => setManualLocation(prev => ({ ...prev, lng: value ?? '' }))}
										/>
									</Form.Item>
								</Col>
								<Col xs={10} style={{ display: "flex", gap: 8, marginBottom: 24 }}>
							<Button
										type="primary"
										onClick={handleNavigateToCoordinates}
										style={{ flex: 1 }}
							>
										Navigate
							</Button>
									<Tooltip title="Get Current Location">
										<Button
											type="default"
											onClick={handleGetCurrentLocation}
											loading={isLocating}
										>
											📍
										</Button>
									</Tooltip>
								</Col>
							</Row>
						</Col>
					</Row>
					{showGeofenceUI && (
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col xs={24}>
								<Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
									Generate Geofence (Buffer Zone)
								</Typography.Text>
								<Typography.Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
									Enter start and end coordinates, then specify buffer distance in meters to create a geofence around the route.
								</Typography.Text>
								<Row gutter={8} align="bottom">
									<Col xs={5}>
										<Form.Item label="Start Latitude">
											<InputNumber
												style={{ width: "100%" }}
												placeholder="Start Lat"
												min={-90}
												max={90}
												step={0.000001}
												value={routeStart.lat}
												onChange={(value) => setRouteStart(prev => ({ ...prev, lat: value ?? '' }))}
											/>
										</Form.Item>
									</Col>
									<Col xs={5}>
										<Form.Item label="Start Longitude">
											<InputNumber
												style={{ width: "100%" }}
												placeholder="Start Lng"
												min={-180}
												max={180}
												step={0.000001}
												value={routeStart.lng}
												onChange={(value) => setRouteStart(prev => ({ ...prev, lng: value ?? '' }))}
											/>
										</Form.Item>
									</Col>
									<Col xs={5}>
										<Form.Item label="End Latitude">
											<InputNumber
												style={{ width: "100%" }}
												placeholder="End Lat"
												min={-90}
												max={90}
												step={0.000001}
												value={routeEnd.lat}
												onChange={(value) => setRouteEnd(prev => ({ ...prev, lat: value ?? '' }))}
											/>
										</Form.Item>
									</Col>
									<Col xs={5}>
										<Form.Item label="End Longitude">
											<InputNumber
												style={{ width: "100%" }}
												placeholder="End Lng"
												min={-180}
												max={180}
												step={0.000001}
												value={routeEnd.lng}
												onChange={(value) => setRouteEnd(prev => ({ ...prev, lng: value ?? '' }))}
											/>
										</Form.Item>
									</Col>
									<Col xs={4} style={{ display: "flex", alignItems: "flex-end", marginBottom: 24 }}>
										<Tooltip title="Generate Route First">
											<Button
												type="primary"
												onClick={handleGenerateRoute}
												loading={isGeneratingRoute}
												style={{ width: "100%" }}
											>
												🚗
											</Button>
										</Tooltip>
									</Col>
								</Row>
								<Row gutter={8} align="bottom">
									<Col xs={11}>
										<Form.Item label="Left Distance (meters)">
											<InputNumber
												style={{ width: "100%" }}
												placeholder="Left meters"
												min={0}
												step={1}
												value={geofenceLeftMeters}
												onChange={(value) => setGeofenceLeftMeters(value ?? '')}
											/>
										</Form.Item>
									</Col>
									<Col xs={11}>
										<Form.Item label="Right Distance (meters)">
											<InputNumber
												style={{ width: "100%" }}
												placeholder="Right meters"
												min={0}
												step={1}
												value={geofenceRightMeters}
												onChange={(value) => setGeofenceRightMeters(value ?? '')}
											/>
										</Form.Item>
									</Col>
									<Col xs={2} style={{ display: "flex", alignItems: "flex-end", marginBottom: 24 }}>
										<Tooltip title="Generate Geofence">
											<Button
												type="primary"
												onClick={handleGenerateGeofence}
												style={{ width: "100%" }}
											>
												🛡️
											</Button>
										</Tooltip>
									</Col>
								</Row>
							</Col>
						</Row>
					)}

					<div style={{ marginBottom: 24 }}>
						<Typography.Text strong>
							Draw Project Site Area
						</Typography.Text>
						<MapContainer
							key={selectedSiteId ?? "new"}
							center={mapCenter}
							zoom={mapZoom}
							whenCreated={(mapInstance) => {
								mapRef.current = mapInstance;
							}}
							style={{ height: 400, width: "100%", marginTop: 12, borderRadius: 8 }}
						>
							<LocateControl />
							<TileLayer
								attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
								url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
							/>
							<FeatureGroup ref={featureGroupRef}>
								<EditControl
									position="topright"
									onCreated={handleDrawCreated}
									onEdited={handleDrawEdited}
									onDeleted={handleDrawDeleted}
									draw={{
										polyline: false,
										circle: false,
										circlemarker: false,
										marker: false,
										rectangle: true,
										polygon: true,
									}}
									edit={{
										edit: true,
										remove: true,
									}}
								/>
								{drawnCoordinates.length >= 2 ? (
									<Polygon
										positions={[...drawnCoordinates, drawnCoordinates[0]]}
										pathOptions={{ color: "#1677ff", fillColor: "#4096ff", fillOpacity: 0.2 }}
									/>
								) : null}
							</FeatureGroup>
							{geofenceCoordinates.length > 0 ? (
								<Polygon
									positions={geofenceCoordinates}
									pathOptions={{ color: "#ff4d4f", fillColor: "#ff7875", fillOpacity: 0.3, weight: 2 }}
								/>
							) : null}
						{manualLocation.lat && manualLocation.lng && 
							Number.isFinite(parseFloat(manualLocation.lat)) && 
							Number.isFinite(parseFloat(manualLocation.lng)) ? (
							<Marker position={[parseFloat(manualLocation.lat), parseFloat(manualLocation.lng)]}>
								<Popup>
									<div>
										<strong>Location</strong>
										<div>Lat: {parseFloat(manualLocation.lat).toFixed(6)}</div>
										<div>Lng: {parseFloat(manualLocation.lng).toFixed(6)}</div>
										<div style={{ marginTop: 8 }}>
											<a
												href={`https://www.google.com/maps?q=${parseFloat(manualLocation.lat)},${parseFloat(manualLocation.lng)}`}
												target="_blank"
												rel="noopener noreferrer"
											>
												Open in Google Maps
											</a>
										</div>
									</div>
								</Popup>
							</Marker>
						) : null}
						</MapContainer>
					</div>

					<Form.List name="points">
						{(fields, { add, remove }) => {
							const dataSource = fields.map((field) => ({ ...field, key: field.key }));
							const columns = [
								{
									title: "#",
									width: 60,
									render: (_, __, index) => index + 1,
								},
								{
									title: "Latitude",
									dataIndex: "Latitude",
									render: (_, field) => (
										<Form.Item
											name={[field.name, "Latitude"]}
											fieldKey={[field.fieldKey, "Latitude"]}
											rules={[{ required: true, message: "Provide latitude" }]}
											style={{ marginBottom: 0 }}
										>
											<InputNumber
												style={{ width: "100%" }}
												placeholder="Latitude"
												min={-90}
												max={90}
												step={0.000001}
											/>
										</Form.Item>
									),
								},
								{
									title: "Longitude",
									dataIndex: "Longitude",
									render: (_, field) => (
										<Form.Item
											name={[field.name, "Longitude"]}
											fieldKey={[field.fieldKey, "Longitude"]}
											rules={[{ required: true, message: "Provide longitude" }]}
											style={{ marginBottom: 0 }}
										>
											<InputNumber
												style={{ width: "100%" }}
												placeholder="Longitude"
												min={-180}
												max={180}
												step={0.000001}
											/>
										</Form.Item>
									),
								},
								{
									title: "Point Order",
									dataIndex: "PointOrder",
									render: (_, field, index) => (
										<Form.Item
											name={[field.name, "PointOrder"]}
											fieldKey={[field.fieldKey, "PointOrder"]}
											style={{ marginBottom: 0 }}
											initialValue={index}
										>
											<InputNumber min={0} style={{ width: "100%" }} />
										</Form.Item>
									),
								},
								{
									title: "Actions",
									key: "actions",
									width: 120,
									render: (_, field) => (
										<Space>
											<Button
												type="link"
												danger
												onClick={() => remove(field.name)}
												disabled={fields.length === 1}
											>
												Remove
											</Button>
										</Space>
									),
								},
							];
							return (
								<>
									<Table
										dataSource={dataSource}
										columns={columns}
										pagination={false}
										scroll={{ x: true }}
									/>
									<Button
										type="dashed"
										block
										icon={<PlusOutlined />}
										onClick={() => add(createEmptyPoint(fields.length))}
										style={{ marginTop: 16 }}
									>
										Add Point
									</Button>
								</>
							);
						}}
					</Form.List>
				</Form>
			</Card>

		</div>
	);
};

export default ProjectSiteArea;

